import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PropertyCard from "../components/PropertyCard";
import SecondaryListings from "../components/SecondaryListings";
import { fetchProperties, fetchPortfolio } from "../lib/api";
import { getUser, saveUser } from "../lib/auth";
import { gbp } from "../lib/format";
import { Wallet, Sparkles, MapPin, Building2, Store, Coins } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const CITIES = ["All", "London", "Manchester", "Birmingham"];
const TYPES = ["All", "Office", "Retail", "Industrial"];

export default function Marketplace() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [city, setCity] = useState("All");
  const [assetType, setAssetType] = useState("All");
  const [wallet, setWallet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("primary");

  useEffect(() => {
    const u = getUser();
    if (!u) {
      navigate("/", { replace: true });
      return;
    }
    setWallet(u.wallet_gbp);
    (async () => {
      try {
        const p = await fetchPortfolio(u.id);
        setWallet(p.metrics.wallet_gbp);
        saveUser({ ...u, wallet_gbp: p.metrics.wallet_gbp });
      } catch (e) { /* ignore */ }
    })();
  }, [navigate]);

  useEffect(() => {
    setLoading(true);
    fetchProperties(city, assetType)
      .then((p) => setProperties(p))
      .finally(() => setLoading(false));
  }, [city, assetType]);

  useEffect(() => {
    // Unfiltered catalog for the Sell Fraction form
    fetchProperties().then(setAllProperties).catch(() => {});
  }, []);

  const stats = useMemo(() => {
    if (!properties.length) return { avgYield: 0, totalGav: 0 };
    const avg = properties.reduce((s, p) => s + p.projected_yield_pct, 0) / properties.length;
    const gav = properties.reduce((s, p) => s + p.valuation_gbp, 0);
    return { avgYield: avg, totalGav: gav };
  }, [properties]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketplace-page">
      <Header />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <div className="overline">Marketplace</div>
              <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
                Institutional UK commercial real estate,{" "}
                <span className="italic" style={{ color: "#D4AF37" }}>fractionalised.</span>
              </h1>
              <p className="mt-4 max-w-xl text-base text-slate-500">
                Curated Grade A assets across London, Manchester, and Birmingham. Allocate from £5,000 per position.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 md:min-w-[420px]" data-testid="marketplace-stats">
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-2">
                  <Wallet className="h-3.5 w-3.5 brickstok-gold" />
                  <div className="overline">Wallet</div>
                </div>
                <div className="mt-2 font-display text-xl font-semibold text-slate-900 font-mono-num" data-testid="marketplace-wallet">
                  {gbp(wallet)}
                </div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 brickstok-gold" />
                  <div className="overline">Avg Yield</div>
                </div>
                <div className="mt-2 font-display text-xl font-semibold text-slate-900 font-mono-num">
                  {stats.avgYield.toFixed(1)}%
                </div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <div className="overline">Total GAV</div>
                <div className="mt-2 font-display text-xl font-semibold text-slate-900 font-mono-num">
                  {gbp(stats.totalGav, { compact: true })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-slate-100/70 p-1" data-testid="marketplace-tabs">
            <TabsTrigger value="primary" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-primary">
              <Store className="mr-2 h-3.5 w-3.5" /> Primary Marketplace
            </TabsTrigger>
            <TabsTrigger value="secondary" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-secondary">
              <Coins className="mr-2 h-3.5 w-3.5" /> Secondary Listings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="primary" className="mt-6" data-testid="primary-tab-content">
            <div className="flex flex-wrap items-end gap-4 rounded-md border border-slate-200 bg-white p-5" data-testid="marketplace-filters">
              <div className="min-w-[180px] flex-1 max-w-xs">
                <div className="overline mb-2 flex items-center gap-1.5"><MapPin className="h-3 w-3 brickstok-gold" /> Location</div>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="filter-city">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`filter-city-option-${c}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[180px] flex-1 max-w-xs">
                <div className="overline mb-2 flex items-center gap-1.5"><Building2 className="h-3 w-3 brickstok-gold" /> Asset Type</div>
                <Select value={assetType} onValueChange={setAssetType}>
                  <SelectTrigger data-testid="filter-asset-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`filter-asset-type-option-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto text-sm text-slate-500">
                <span className="font-mono-num text-slate-900 font-semibold" data-testid="filter-result-count">{properties.length}</span>{" "}
                {properties.length === 1 ? "asset" : "assets"} available
              </div>
            </div>

            <div className="pt-8">
              {loading ? (
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="animate-pulse rounded-lg border border-slate-200 bg-white">
                      <div className="aspect-[16/10] w-full bg-slate-100" />
                      <div className="space-y-3 p-6">
                        <div className="h-3 w-16 bg-slate-100" />
                        <div className="h-6 w-3/4 bg-slate-100" />
                        <div className="h-3 w-1/2 bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : properties.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="no-properties-state">
                  <div className="overline">No matches</div>
                  <h3 className="mt-2 font-display text-xl font-semibold text-slate-900">No assets match these filters</h3>
                  <p className="mt-2 text-sm text-slate-500">Try broadening your city or asset type.</p>
                </div>
              ) : (
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="properties-grid">
                  {properties.map((p) => (
                    <PropertyCard key={p.id} property={p} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="secondary" className="mt-6" data-testid="secondary-tab-content">
            <SecondaryListings
              properties={allProperties.length ? allProperties : properties}
              onWalletChange={(w) => {
                setWallet(w);
                const u = getUser();
                if (u) saveUser({ ...u, wallet_gbp: w });
              }}
            />
          </TabsContent>
        </Tabs>
      </section>

      <div className="pb-10" />
      <Footer />
    </div>
  );
}
